import React, { Suspense } from 'react';

import LinkIcon from '@atlaskit/icon/core/link';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { ProductKeys } from '../../constants/product-keys';
import { type ProductConfigs, useProductConfigs } from '../../constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../constants/schemas/3p-config';
import {
	MICROSOFT_INTEGRATION_KEY,
	getProductFromMicrosoftResourceType,
} from '../../utils/search-result-utils';
import { getFileTypeBrandIcon } from './file-type-brand-icon';
import { FileTypeIconTile, getFileTypeIconFor3P } from './file-type-icon-tile';

export const IconFor3P = ({
	productConfigs,
	...props
}: {
	product?: string;
	resourceType: string;
	size?: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';
	subtype?: string | null;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	productConfigs?: ProductConfigs;
}): React.JSX.Element => {
	return productConfigs ? (
		<IconFor3PWithProductConfigs {...props} productConfigs={productConfigs} />
	) : (
		<IconFor3PWithLocalProductConfigs {...props} />
	);
};

type IconFor3PProps = {
	product?: string;
	resourceType: string;
	size?: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';
	subtype?: string | null;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
};

const IconFor3PWithLocalProductConfigs = (props: IconFor3PProps): React.JSX.Element => {
	const productConfigs = useProductConfigs();

	return <IconFor3PWithProductConfigs {...props} productConfigs={productConfigs} />;
};

const IconFor3PWithProductConfigs = ({
	product,
	resourceType,
	size,
	subtype,
	thirdPartyConfigs,
	productConfigs,
}: IconFor3PProps & { productConfigs: ProductConfigs }): React.JSX.Element => {
	if (!product) {
		return <LinkIcon color="currentColor" spacing="spacious" label="" />;
	}
	let productKey = product === 'google' ? ProductKeys.GoogleDrive : product;
	/**
	 * Temporary fix for Microsoft integrationKey
	 */
	if (product === MICROSOFT_INTEGRATION_KEY) {
		productKey = getProductFromMicrosoftResourceType(product, resourceType) ?? productKey;
	}

	let IconComponent;

	// For Forge connectors, prioritize dynamic API icons over static hardcoded ones
	if (isForgeConnectorProductKey(productKey)) {
		const dynamicConfig = thirdPartyConfigs[productKey];
		if (dynamicConfig) {
			IconComponent = dynamicConfig.searchResultIcon || dynamicConfig.icon;
		}
	}

	// Fallback to static products (for non-Forge or if dynamic config not found)
	if (!IconComponent) {
		IconComponent =
			productConfigs[productKey as ProductKeys]?.SearchResultIcon ||
			productConfigs[productKey as ProductKeys]?.Icon;
	}

	const FileTypeGlyph = getFileTypeIconFor3P(productKey, resourceType, subtype);
	const tileLabel = subtype ? `${productKey} ${subtype} icon` : `${productKey} icon`;
	const legacyIcon = FileTypeGlyph ? (
		<FileTypeIconTile Glyph={FileTypeGlyph} label={tileLabel} size={size} />
	) : IconComponent ? (
		// 'medium' (32px) is this component's default size, matching FileTypeIconTile and the
		// BrandIcon branch below. Without it the product config's own default leaks through, and
		// every `searchResultIcon` config declares 'large' — a legacy spelling of 32px, since the
		// old bucket scale clamped both 'large' and 'xlarge' to a 32px box. On the accurate scale
		// 'large' really is 40px, so relying on that default would silently grow these icons.
		<IconComponent label="" size={size ?? 'medium'} />
	) : (
		<LinkIcon color="currentColor" spacing="spacious" label="" />
	);

	const BrandIcon = fg('rovo-search-team-eu-26-brand-icon-library')
		? getFileTypeBrandIcon(productKey, subtype)
		: null;
	return BrandIcon ? (
		<Suspense fallback={legacyIcon}>
			<BrandIcon label={FileTypeGlyph ? tileLabel : ''} size={size ?? 'medium'} />
		</Suspense>
	) : (
		legacyIcon
	);
};
